import * as React from "react";
import { useSelector } from "react-redux";
import { useDataLoader, usePerformAction } from "react-remote-data-hooks";
import api from "../../../../api";
import { ContainerLoader } from "../../../../components/loader";
import {
  ApiResponse,
  Lead,
  LeadQuality,
  LeadStatuses,
  useHasPermission,
  AppPermissions,
} from "../../../../core";
import moment from "moment";
import classnames from "classnames";
import defaultUserImage from "../../../../assets/images/user-noimage.png";
import { State } from "../../../../core/store/interfaces";
import { UserAvatar } from "../../../../components/render-file/UserAvatar";

interface ILinkedLeadsProps {
  id: string;
}

const LinkedLeads: React.FunctionComponent<ILinkedLeadsProps> = (props) => {
  const leadState = useSelector((state: State) => state.lead);

  const canLinkLead =
    useHasPermission(AppPermissions.LinkLead) &&
    leadState?.leadDetails?.isEditable;
  const canUnlinkLead =
    useHasPermission(AppPermissions.UnlinkLead) &&
    leadState?.leadDetails?.isEditable;

  const {
    data: linkedLeadsData,
    loading: linkedLeadsLoading,
    loaded: linkedLeadsLoaded,
    reload: reloadLinkedLeads,
  } = useDataLoader<ApiResponse<Lead[]>>(() =>
    api.lead.getLinkedLeads(props.id)
  );
  const {
    data: linkableLeadsData,
    loading: linkableLeadsLoading,
    loaded: linkableLeadsLoaded,
    reload: reloadLinkableLeads,
  } = useDataLoader<ApiResponse<Lead[]>>(() =>
    api.lead.getLinkableLeads(props.id)
  );

  const { performAction: unlinkLead, isPerforming: linking } = usePerformAction(
    (id: string) => api.lead.unlinkLead(id),
    () => {
      reloadLinkedLeads();
      reloadLinkableLeads();
    }
  );

  const { performAction: linkLead, isPerforming: unlinking } = usePerformAction(
    (id: string) => api.lead.linkLead(id, props.id),
    () => {
      reloadLinkedLeads();
      reloadLinkableLeads();
    }
  );

  const [leadsSelected, setLeadsSelected] = React.useState(true);
  const [selectedTab, setSelectedTab] = React.useState("leads");

  if (
    !linkedLeadsLoaded ||
    !linkableLeadsLoaded ||
    linkedLeadsLoading ||
    linkableLeadsLoading
  ) {
    return <ContainerLoader height={100}></ContainerLoader>;
  }

  const linkedDeals = linkedLeadsData.data.filter((lead) => lead.isDeal);
  const linkedLeads = linkedLeadsData.data.filter((lead) => !lead.isDeal);

  const linkableDeals = linkableLeadsData.data.filter((lead) => lead.isDeal);
  const linkableLeads = linkableLeadsData.data.filter((lead) => !lead.isDeal);

  return (
    <>
      <div className="px-4 py-3 bg-gray-100 border-top border-bottom">
        <h6 className="mb-0">Linked Leads/Opportunities</h6>
      </div>
      <div className="p-4">
        <div className="tabButton d-flex w-100 text-center font-sm font-weight-semibold cursor-pointer mb-4">
          <div
            className={classnames({
              "tabButton__item flex-fill": true,
              "tabButton__item--active": selectedTab === "leads",
            })}
            onClick={() => setSelectedTab("leads")}
          >
            Leads ({linkedLeads.length})
          </div>
          <div
            className={classnames({
              "tabButton__item flex-fill": true,
              "tabButton__item--active": selectedTab === "enquiries",
            })}
            onClick={() => setSelectedTab("enquiries")}
          >
            Opportunities ({linkedDeals.length})
          </div>
          <div
            className={classnames({
              "tabButton__item flex-fill": true,
              "tabButton__item--active": selectedTab === "source",
            })}
            onClick={() => setSelectedTab("source")}
          >
            Lead From Source
          </div>
        </div>
        <div className="tabButtonContent">
          <div
            className={classnames({
              tabButtonContent__item: true,
              "tabButtonContent__item--active": selectedTab === "leads",
            })}
          >
            {linkedLeads.length > 0 && (
              <>
                <h6 className="font-xxs text-gray-500 text-uppercase py-2 mb-0">
                  Linked Leads ({linkedLeads.length})
                </h6>
                {linkedLeads.map((lead) => (
                  <LinkedLeadItem
                    linked
                    lead={lead}
                    key={lead._id}
                    onChange={(id) =>
                      canUnlinkLead ? unlinkLead(id) : undefined
                    }
                    isChanging={linking || unlinking}
                    canLink={canUnlinkLead}
                  />
                ))}
              </>
            )}
          </div>
          <div
            className={classnames({
              tabButtonContent__item: true,
              "tabButtonContent__item--active": selectedTab === "enquiries",
            })}
          >
            {linkedDeals.length > 0 && (
              <>
                <h6 className="font-xxs text-gray-500 text-uppercase py-2 mb-0">
                  Linked Opportunities ({linkedDeals.length})
                </h6>
                {linkedDeals.map((lead) => (
                  <LinkedLeadItem
                    linked
                    lead={lead}
                    key={lead._id}
                    onChange={(id) =>
                      canUnlinkLead ? unlinkLead(id) : undefined
                    }
                    isChanging={linking || unlinking}
                    canLink={canUnlinkLead}
                  />
                ))}
              </>
            )}
            {linkedDeals.length > 0 && (
              <>
                <h6 className="font-xxs text-gray-500 text-uppercase py-2 mb-0">
                  Other Opportunities ({linkableDeals.length})
                </h6>
                {linkableDeals.map((lead) => (
                  <LinkedLeadItem
                    lead={lead}
                    key={lead._id}
                    onChange={(id) => (canLinkLead ? linkLead(id) : undefined)}
                    isChanging={linking || unlinking}
                    canLink={canLinkLead}
                  />
                ))}
              </>
            )}
          </div>
          <div
            className={classnames({
              tabButtonContent__item: true,
              "tabButtonContent__item--active": selectedTab === "source",
            })}
          >
            {leadState?.leadDetails?.sourceInputData && (
              <>
                <h6 className="font-xxs text-gray-500 text-uppercase py-2 mb-0">
                  Leads From Source
                </h6>

                <LeadFromSource
                  lead={leadState?.leadDetails}
                  key={leadState?.leadDetails?._id}
                  onChange={(id) =>
                    canUnlinkLead ? unlinkLead(id) : undefined
                  }
                  isChanging={linking || unlinking}
                />
              </>
            )}
          </div>
          <h6 className="font-xxs text-gray-500 text-uppercase py-2 mb-0">
            Other Leads & Opportunities (
            {linkableLeads.length + linkableDeals.length})
          </h6>
          {linkableLeads.map((lead) => (
            <LinkedLeadItem
              lead={lead}
              key={lead._id}
              onChange={(id) => (canLinkLead ? linkLead(id) : undefined)}
              isChanging={linking || unlinking}
              canLink={canLinkLead}
            />
          ))}
          {linkableDeals.map((lead) => (
            <LinkedLeadItem
              lead={lead}
              key={lead._id}
              onChange={(id) => (canLinkLead ? linkLead(id) : undefined)}
              isChanging={linking || unlinking}
              canLink={canLinkLead}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default LinkedLeads;

const LinkedLeadItem = (props: {
  lead: Lead;
  linked?: boolean;
  onChange?: (id: string) => void;
  isChanging?: boolean;
  canLink?: boolean;
}) => {
  const [isPopupOpen, setPopupOpen] = React.useState(false);
  return (
    <div className="border rounded p-2 row no-gutters mb-2">
      <div className="col-6 p-1">
        <h6 className="mb-2 font-weight-semibold">
          {props.lead.title}
          <LeadStatusBadge status={props.lead.currentStatus} />
          {props.lead.quality && (
            <LeadQualityBadge quality={props.lead.quality} />
          )}
        </h6>
        {/* <p className="font-xs mb-2 font-weight-semibold mb-0">
          {props.lead.leadType?.name}
        </p> */}
      </div>
      <div className="col-6 p-1">
        <div className="d-flex justify-content-end align-items-center">
          <ul className="list-unstyled px-0 mb-0 avatar-user-list d-flex">
            {props.lead.currentCRMAgent && (
              <li className="rounded-circle overflow-hidden border border-white border-width-2 bg-gray-300">
                <UserAvatar
                  id={props.lead.currentCRMAgent?._id}
                  className="img-fluid img-cover"
                  alt=""
                  title={props.lead.currentCRMAgent.name}
                  imageUrl={props.lead?.currentCRMAgent?.avatar?.location}
                  image={props.lead?.currentSalesPerson?.avatar}
                ></UserAvatar>
              </li>
            )}
            {props.lead.currentSalesPerson && (
              <li className="rounded-circle overflow-hidden border border-white border-width-2 bg-gray-300">
                <UserAvatar
                  id={props.lead.currentSalesPerson?._id}
                  className="img-fluid img-cover"
                  alt=""
                  title={props.lead.currentSalesPerson.name}
                  imageUrl={props.lead?.currentSalesPerson?.avatar?.location}
                  image={props.lead?.currentSalesPerson?.avatar}
                ></UserAvatar>
              </li>
            )}
          </ul>
          <div className="dropdown">
            {props.canLink && (
              <i
                className="icon-link icon-box--sm font-xs d-inline-flex justify-content-center align-items-center border rounded ml-2"
                onClick={() => setPopupOpen(!isPopupOpen)}
              ></i>
            )}

            <div
              className={classnames({
                "dropdown-menu dropdown-menu-right dropdown-shadow sm rounded font-sm border-0 w-100": true,
                in: isPopupOpen,
              })}
            >
              <div className="py-3 px-4">
                <h6 className="font-weight-semibold mb-2 font-xs">
                  Are you sure? {props.linked ? "unlink" : "link"} this lead
                </h6>
                <div className="row gutter-5">
                  <div className="col-6">
                    <button
                      className="btn btn-outline-secondary btn-block font-normal font-weight-semibold py-1 px-4"
                      disabled={props.isChanging}
                      onClick={() => setPopupOpen(false)}
                    >
                      No
                    </button>
                  </div>
                  <div className="col-6">
                    <button
                      className="btn btn-outline-secondary btn-hover-primary btn-block font-normal font-weight-semibold py-1 px-4"
                      disabled={props.isChanging}
                      onClick={() => props.onChange?.(props.lead._id!)}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!props.lead.isDeal && (
        <>
          <div className="col-3 p-1">
            <div className="font-xxs text-gray-500 mb-1">Date</div>
            <div className="font-xs">
              {props.lead.createdAt
                ? moment(props.lead.createdAt).format("DD/MMM/YYYY")
                : ""}
            </div>
          </div>
          <div className="col-3 p-1">
            <div className="font-xxs text-gray-500 mb-1">Lead Type</div>
            <div className="font-xs">{props.lead.leadType?.name}</div>
          </div>
          <div className="col-3 p-1">
            <div className="font-xxs text-gray-500 mb-1">Lead touch point</div>
            <div className="font-xs">{props.lead.initialTouchPoint?.name}</div>
          </div>
          <div className="col-3 p-1">
            <div className="font-xxs text-gray-500 mb-1">Source</div>
            <div className="font-xs">{props.lead.businessSource?.name}</div>
          </div>
        </>
      )}
      {props.lead.isDeal && (
        <>
          <div className="col-6 font-xxs pb-1 pt-0 px-1 align-self-end">
            <span className="text-gray-500">Date:</span>{" "}
            {props.lead.createdAt
              ? moment(props.lead.createdAt).format("DD/MMM/YYYY")
              : ""}
          </div>
          <div className="col-6 pb-1 pt-0 px-1 text-right align-self-end">
            <div className="icon-box--xs bg-shamrock rounded-circle d-inline-flex align-items-center justify-content-center">
              <i className=" icon-chevron-right text-white font-xxs"></i>
            </div>
          </div>
          <div className="col-12 p-1">
            <div className="progress rounded-sm bg-gray-400 position-relative d-flex overflow-hidden">
              <div
                className="progress-bar position-absolute left-0 top-0 bg-primary h-100"
                style={{ width: "30%;" }}
              ></div>
              <div className="progress-split position-relative zIndex-1 flex-fill"></div>
              <div className="progress-split position-relative zIndex-1 flex-fill"></div>
              <div className="progress-split position-relative zIndex-1 flex-fill"></div>
              <div className="progress-split position-relative zIndex-1 flex-fill"></div>
              <div className="progress-split position-relative zIndex-1 flex-fill"></div>
              <div className="progress-split position-relative zIndex-1 flex-fill"></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const LeadStatusBadge = (props: { status?: LeadStatuses }) => {
  const ignoreStatuses: LeadStatuses[] = [LeadStatuses.Deal, LeadStatuses.Lead];
  const wonStatues: LeadStatuses[] = [LeadStatuses.Won];
  const neutralStatues: LeadStatuses[] = [];
  const lostStatues = [LeadStatuses.Lost, LeadStatuses.Spam];
  if (!props.status || ignoreStatuses.includes(props.status)) {
    return <></>;
  }
  const classes = [
    "ml-4",
    "border",
    "rounded",
    "font-xs",
    "px-1",
    "text-capitalize",
  ];
  if (wonStatues.includes(props.status)) {
    classes.push("border-success", "text-success");
  }
  if (neutralStatues.includes(props.status)) {
    classes.push("border-primary", "text-primary");
  }
  if (lostStatues.includes(props.status)) {
    classes.push("border-danger", "text-danger");
  }
  return (
    <span className={classes.join(" ")}>{props.status?.toUpperCase()}</span>
  );
};

const LeadQualityBadge = (props: { quality?: LeadQuality }) => {
  if (!props.quality || props.quality === LeadQuality.NotIntrested) {
    return <></>;
  }
  const classes = [
    "ml-4",
    "border",
    "rounded",
    "font-xs",
    "px-1",
    "text-capitalize",
  ];
  if (props.quality === LeadQuality.Interested) {
    classes.push("border-primary", "text-primary");
  }
  return (
    <span className={classes.join(" ")}>{props.quality?.toUpperCase()}</span>
  );
};

const LeadFromSource = (props: {
  lead?: Lead;
  linked?: boolean;
  onChange?: (id: string) => void;
  isChanging?: boolean;
}) => {
  // show more and less accordion has been turned off 
  // const [isDetailsOpen, setDetailsOpen] = React.useState(true);
  return (
    <div className="border rounded   mb-2">
      <div className="p-2 row no-gutters mb-2">
        <div className="col-6 p-1">
          <p className="font-xs mb-2 font-weight-semibold mb-0">
            {props.lead?.sourceInputData?.year}{" "}
            {props.lead?.sourceInputData?.model}{" "}
            {props.lead?.sourceInputData?.variant}
          </p>
        </div>
        <div className="col-6 p-1">
          <div className="d-flex justify-content-end align-items-center"></div>
        </div>

        <div className="col-3 p-1">
          <div className="font-xxs text-gray-500 mb-1">Date</div>
          <div className="font-xs">
            {props.lead?.createdAt
              ? moment(props.lead?.createdAt).format("DD/MMM/YYYY")
              : ""}
          </div>
        </div>
        <div className="col-3 p-1">
          <div className="font-xxs text-gray-500 mb-1">Lead Type</div>
          <div className="font-xs">{props.lead?.sourceInputData?.leadType}</div>
        </div>
        <div className="col-3 p-1">
          <div className="font-xxs text-gray-500 mb-1">Lead touch point</div>
          <div className="font-xs">
            {props.lead?.sourceInputData?.initialTouchPoint}
          </div>
        </div>
        <div className="col-3 p-1">
          <div className="font-xxs text-gray-500 mb-1">Source</div>
          <div className="font-xs">
            {props.lead?.sourceInputData?.businessSource}
          </div>
        </div>
        {/* {!isDetailsOpen && (
          <div className="col-12 p-1 text-align--right">
            <div className="font-xxs mb-1 text-align--right text-gray-500 text-right">
              <span
                className="cursor-pointer"
                onClick={() => setDetailsOpen(true)}
              >
                SHOW MORE <i className="icon-chevron-down"></i>
              </span>
            </div>
          </div>
        )} */}
      </div>
      {
      // isDetailsOpen &&
       (
        <>
          <div className="col-12 p-1 text-align--right border-top">
            <div className="mt-2 d-flex flex-wrap px-2 py-1 align-items-center flex-wrap">
              {props.lead?.sourceInputData?.prefix && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Title
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.prefix}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.firstName && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    First Name
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.firstName}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.middleName && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Middle Name
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.middleName}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.lastName && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Last Name
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.lastName}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.email && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Email
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.email}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.phone && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Phone
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.phone}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.title && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                  Project/ Property
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.title}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}

              {props.lead?.sourceInputData?.leadIntent && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Lead Intent
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.leadIntent}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.leadType && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Lead Type
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.leadType}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.initialTouchPoint && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Initial Touch Point
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.initialTouchPoint}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.businessSource && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Source of business
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.businessSource}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.year && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Year
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.year}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.model && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Model
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.model}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.model && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Variant
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.variant}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.leadChannel && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Lead Channel
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.leadChannel}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.showroom && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Showroom
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.showroom}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.requestedDate && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Requested Date
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {moment(
                        props.lead?.sourceInputData?.requestedDate
                      ).format("DD/MMM/YYYY")}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.requestedTime && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Requested Time
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.requestedTime}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.comEbCompanyName && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Company name
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.comEbCompanyName}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.workSpaceType && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Workspace type
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.workSpaceType}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.workSpaceAreaSqFt && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Workspace Area sqft
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.workSpaceAreaSqFt}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.workSpaceSeat && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Workspace seat
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.workSpaceSeat}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.location && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Location
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.location}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.cityName && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    City name
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.cityName}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.otherCity && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Other city
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.otherCity}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.budget && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Budget
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.budget}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.designation && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Designation
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.designation}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.requiredFrom && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Required from
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.requiredFrom}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.companyName && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Company name
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.companyName}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
              {props.lead?.sourceInputData?.note && (
                <>
                  <div className="col pl-0 pr-2 contact-label font-xs text-gray-500">
                    Comment
                  </div>
                  <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
                    <div className="font-sm font-weight-semibold">
                      {props.lead?.sourceInputData?.note}
                    </div>
                  </div>
                  <div className="mb-4 w-100"></div>
                </>
              )}
            </div>
          </div>
          {/* {isDetailsOpen && (
            <div className="col-12 p-1 text-align--right">
              <div className="font-xxs mb-1 text-align--right text-gray-500 text-right mr-2">
                <span
                  className="cursor-pointer"
                  onClick={() => setDetailsOpen(false)}
                >
                  SHOW LESS <i className="icon-chevron-up"></i>
                </span>
              </div>
            </div>
          )} */}
        </>
      )}
    </div>
  );
};
